<template>
    <v-dialog :fullscreen="isMobile" v-model="dialog" max-width="700"
        :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('common.upload_links') }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="12">
                        <small>{{ $t('common.upload_links_subtitle') }}</small>
                        <br>
                        <small v-html="$t('common.upload_links_help')" />
                    </v-col>
                </v-row>
                <v-row v-for="(link, i) in links" :key="i" align="center" dense>
                    <v-col cols="4" md="5">
                        <kimsa-text-field :label="`${$t('common.name')} ${i + 1}`"
                            :placeholder="$t('common.write_here')" bolder-label outlined dense :value="link.name"
                            @change="link.name = $event" />
                    </v-col>
                    <v-col cols="4" md="5">
                        <kimsa-text-field :label="`${$t('common.link')} ${i + 1}`"
                            :placeholder="$t('common.write_here')" bolder-label outlined dense
                            @blur="link.videoId = getYouTubeVideoId(link.videoId)" :value="link.videoId"
                            @change="link.videoId = $event" />
                    </v-col>
                    <v-col cols="2" md="1">
                        <v-btn icon color="#fd0000" :disabled="!link.videoId" @click="goToVideo(link.videoId)"
                            class="mt-3">
                            <v-icon>mdi-youtube</v-icon>
                        </v-btn>
                    </v-col>
                    <v-col cols="2" md="1">
                        <v-btn icon color="error" @click="deleteVideo(i)" class="mt-3">
                            <v-icon>$delete</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" align="right">
                        <v-btn fab color="black" @click="addVideo()" class="mt-3" small dark>
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text" @click="save"
                    :loading="loading">
                    {{ $t('common.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>

import { mapMutations, mapState } from 'vuex'
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { db, Timestamp } from "@/utils/Firebase";

export default {
    name: "YoutubeUploadLinksDialog",
    components: { KimsaTextField },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            linksTemplate: [{ name: '', videoId: '' }],
            links: [],
            linksDefault: null,
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        coachId(vm = this) {
            return vm.currentUser.uid || null
        },
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        show() {
            const vm = this
            vm.setDefaults()
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.links = vm.linksTemplate
        },
        getYouTubeVideoId(url) {
            const pattern = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
            const match = url.match(pattern)
            return match?.[1] ? match[1] : null
        },
        setDefaults() {
            const vm = this
            vm.links = [...vm.account?.links] || vm.linksTemplate
            vm.linksDefault = JSON.parse(JSON.stringify(vm.links))
        },
        goToVideo(videoId) {
            const vm = this
            window.open('https://www.youtube.com/watch?v=' + videoId, '_blank')
        },
        deleteVideo(i) {
            const vm = this;
            vm.links.splice(i, 1)
        },
        addVideo() {
            const vm = this;
            vm.links.push(...vm.linksTemplate)
        },
        hasLinksChanged() {
            const vm = this;
            if (vm.links.length !== vm.linksDefault.length) return true;
            return vm.links.some((link, index) => {
                const defaultLink = vm.linksDefault[index];
                return link.name !== defaultLink.name || link.videoId !== defaultLink.videoId;
            });
        },
        async save() {
            const vm = this
            vm.loading = true

            await db.collection('users').doc(vm.currentUser.uid).update({
                links: vm.links.filter(link => !!link.name && !!link.videoId)
            })

            if (vm.hasLinksChanged()) {
                let studentsRef = await db.collection('users')
                    .where('studentSubscription.coachId', '==', vm.coachId)
                    .where('studentSubscription.paused', '==', false)
                    .get()
                for (let student of studentsRef.docs) {
                    console.log('student',student);
                    if (student.id) await vm.sendMessageToStudent(student.id)
                }
            }

            vm.toast(vm.$t('common.links_saved'))
            vm.loading = false
            vm.hide()
        },
        async sendMessageToStudent(studentId) {
            const vm = this

            let message = vm.$t('common.new_video_upload')

            let coachId = vm.coachId

            let chatRef = await db.collection('privateChats')
                .where('type', '==', 'coach_student')
                .where('participants', 'in', [[coachId, studentId]])
                .get()

            let chatId = null
            if (chatRef.docs.length > 0) {
                chatId = chatRef.docs[0].id
            } else {
                let newChat = await db.collection('privateChats').add({
                    type: 'coach_student',
                    participants: [coachId, studentId]
                })
                chatId = newChat.id
            }

            let payload = {
                message: message,
                from: coachId,
                read: false,
                timestamp: Timestamp.now(),
                type: 'text'
            }

            await db.collection('privateChats').doc(chatId).collection('messages').add(payload)
            await db.collection('privateChats').doc(chatId).update({lastMessage: payload})
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
    }
}

</script>

<style scoped>
.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}

.label {
    font-size: 15px;
    color: #333333;
}
</style>